import React, { FC, useRef, useEffect, useState } from "react";
import { css, Icon } from "@sourceful/shared-components";
import { IconTextNew, IconTextNewProps } from "../iconTextNew/IconTextNew";
import anime from "animejs";
import { ResizeObserver as Polyfill } from "@juggle/resize-observer";

const ResizeObserver =
  typeof window !== "undefined" && window.ResizeObserver ? window.ResizeObserver : Polyfill;

const componentCss = css({
  display: "flex",
  py: "$spacing-fluid-block-s",
  backgroundColor: "$colour-neutral-1-tint-90",

  "@bp-sm": {
    py: "$spacing-fluid-block-xxs",
  },

  variants: {
    mode: {
      Paginated: {},
      Wrap: { flexWrap: "wrap" },
      NoWrap: { flexWrap: "nowrap" },
    },
    isPaginated: {
      true: {
        position: "relative",
      },
    },
  },
});

const maxWidthWrapCss = css({
  display: "flex",
  width: "100%",
  maxw: "$site",
  margin: "0 auto",
  position: "relative",
});

const scrollCss = css({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "$spacing-fluid-block-xxs",

  "& > *": {
    px: "$spacing-fluid-block-xs",
  },

  variants: {
    isPaginated: {
      true: {
        position: "relative",
        zIndex: "1",
        overflowX: "auto",
        justifyContent: "normal",
        scrollSnapType: "x mandatory",
        alignItems: "center",

        "&::-webkit-scrollbar": {
          display: "none",
        },

        "& > *": {
          flex: "0 0 100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          scrollSnapAlign: "start",
          //textAlign: "center",
          px: "$spacing-static-inline-l",
        },
      },
    },
    isLoading: {
      true: {},
    },
    mode: {
      Paginated: {},
      Wrap: { flexWrap: "wrap" },
      NoWrap: { flexWrap: "nowrap", overflowX: "auto", whiteSpace: "nowrap" },
    },
  },
  compoundVariants: [
    {
      isLoading: true,
      mode: "Paginated",
      css: {
        visibility: "hidden",
      },
    },
  ],
});

const scrollButtonsCss = css({
  display: "none",

  variants: {
    isPaginated: {
      true: {
        display: "flex",
        height: "0",
        alignItems: "center",
        justifyContent: "space-between",
        position: "absolute",
        top: "50%",
        left: "0",
        right: "0",
        bottom: "0",
        transform: "translateY(-50%)",
        zIndex: "2",
      },
    },
  },
});

const scrollButtonCss = css({
  cursor: "pointer",
  border: "none",
  variants: {
    left: {
      yes: {},
      no: {
        marginInlineStart: "auto",
      },
    },
  },
});

const srCss = css({
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: "0",
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  borderWidth: 0,
});

const iconTextContainerCss = css({
  display: "flex",
  alignItems: "center",
});

export type StripBannerMode = "Paginated" | "Wrap" | "NoWrap";

export interface StripBannerProps {
  mode: StripBannerMode;
  items: IconTextNewProps[];
}

export const StripBanner: FC<StripBannerProps> = ({ mode = "Paginated", items }) => {
  const [isPaginated, setIsPaginated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const componentRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    //console.log("StripBanner re-render");
  }, [isPaginated]);

  const scrollLeftClick = () => {
    //console.log("StripBanner.scrollLeftClick", scrollRef);

    if (scrollRef.current) {
      const width = Math.floor(scrollRef.current?.clientWidth),
        currentScrollLeft = Math.floor(scrollRef.current.scrollLeft),
        scrollWidth = Math.ceil(scrollRef.current.scrollWidth);

      // console.log(
      //   "StripBanner.scrollLeftClick scrolling",
      //   currentScrollLeft,
      //   scrollRef.current.scrollWidth
      // );

      anime({
        targets: scrollRef.current,
        opacity: 0,
        easing: "linear",
        duration: 250,
        complete: () => {
          // Loop around
          if (currentScrollLeft === 0) {
            scrollRef.current?.scrollBy({ left: scrollWidth });
          } else {
            scrollRef.current?.scrollBy({ left: -width });
          }

          anime({
            targets: scrollRef.current,
            opacity: 1,
            easing: "linear",
            duration: 250,
          });
        },
      });
    }
  };

  const scrollRightClick = () => {
    //console.log("StripBanner.scrollRightClick", scrollRef);

    if (scrollRef.current) {
      const width = Math.ceil(scrollRef.current?.clientWidth),
        currentScrollLeft = Math.ceil(scrollRef.current.scrollLeft),
        currentScrollLeftAndWidth = currentScrollLeft + width,
        scrollWidth = Math.ceil(scrollRef.current.scrollWidth);

      // console.log(
      //   "StripBanner.scrollRightClick scrolling",
      //   currentScrollLeft,
      //   currentScrollLeftAndWidth,
      //   scrollWidth
      // );

      anime({
        targets: scrollRef.current,
        opacity: 0,
        easing: "linear",
        duration: 250,
        complete: () => {
          // Loop around
          if (currentScrollLeftAndWidth >= scrollWidth) {
            scrollRef.current?.scrollBy({ left: -scrollWidth });
          } else {
            scrollRef.current?.scrollBy({ left: width });
          }

          anime({
            targets: scrollRef.current,
            opacity: 1,
            easing: "linear",
            duration: 250,
          });
        },
      });
    }
  };

  const scrollResizeObserver = (entries: ResizeObserverEntry[]) => {
    setIsPaginated(false);

    entries.forEach(function (entry) {
      const el = entry.target,
        containerWidth = componentRef.current?.clientWidth || 0,
        scrollWidth = el.scrollWidth;

      //console.log("scrollResizeObserver", { containerWidth, scrollWidth });

      setIsPaginated(scrollWidth > containerWidth);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (mode !== "Paginated") {
      return;
    }

    const resizeObserver = new ResizeObserver(scrollResizeObserver);

    if (scrollRef.current) {
      resizeObserver.observe(scrollRef.current as Element);
    }

    return () => {
      if (scrollRef.current) {
        resizeObserver.unobserve(scrollRef.current as Element);
      }
    };
  }, [mode]);

  function getMode(mode: StripBannerMode, isPaginated: boolean) {
    if (mode === "Paginated") {
      if (isPaginated) {
        return "Paginated";
      }

      return "NoWrap";
    }

    return mode;
  }

  return (
    <div
      ref={componentRef}
      className={componentCss({
        mode: getMode(mode, isPaginated),
        isPaginated: isPaginated,
      })}
    >
      <div className={maxWidthWrapCss()}>
        <div
          className={scrollButtonsCss({
            isPaginated: isPaginated,
          })}
        >
          <button
            type="button"
            onClick={scrollLeftClick}
            className={scrollButtonCss({ left: "yes" })}
          >
            <span className={srCss()}>Scroll left</span>
            <Icon name="arrow-direction-left" />
          </button>

          <button
            type="button"
            onClick={scrollRightClick}
            className={scrollButtonCss({ left: "no" })}
          >
            <span className={srCss()}>Scroll right</span>
            <Icon name="arrow-direction-right" />
          </button>
        </div>

        <div
          ref={scrollRef}
          data-is-paginated={isPaginated}
          data-is-loading={isLoading}
          className={scrollCss({
            isPaginated: isPaginated,
            isLoading: isLoading,
            mode: getMode(mode, isPaginated),
          })}
        >
          {items.map((x, i) => {
            return (
              <div className={iconTextContainerCss()} key={`sb-it-${i}`}>
                <IconTextNew
                  {...x}
                  css={{
                    "@bp-sm": {
                      svg: {
                        width: "$iconS",
                        height: "$iconS",
                      },
                    },
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StripBanner;
